/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DownloadService, LogService, ModalService, RbacService, UserService } from '../../../../../../core/services';
import { ErrorResponseModel } from '../../../../../../core/models';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { OrganisationsApiService } from '../../../../shared/http';
import { PortalUrlsEnum } from '../../../../shared/enums';
import { environment } from '../../../../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';
import { urlValidation } from '../../../../../../core/directives/validation';

declare let Intercom: any;

@Component({
    selector: 'app-saml',
    templateUrl: './saml.component.html',
    styleUrls: ['./saml.component.scss']
})
export class SamlComponent extends BaseComponent {
    /**
     * Link URL - for entityId, issuerId and ssoUrl.
     */
    public linkUrl: string = environment.production ? PortalUrlsEnum.PRODUCTION : PortalUrlsEnum.NON_PRODUCTION;

    /**
     * Has tier.
     */
    public hasTier: boolean = false;

    /**
     * Is loading state.
     */
    public isLoading: boolean = true;

    /**
     * Active step number.
     */
    public activeStep: number = 1;

    /**
     * Entity ID.
     */
    public entityId: string = this.linkUrl;

    /**
     * Issuer ID.
     */
    public issuerId: string = this.linkUrl;

    /**
     * SSO URL.
     */
    public ssoUrl: string = this.linkUrl + '/api/v1/accounts/auth/saml/callback';

    /**
     * Name ID.
     */
    public nameId: string = 'EmailAddress';

    /**
     * Account form group.
     */
    public formGroup: FormGroup;

    /**
     * Enforce form group.
     */
    public enforceFormGroup: FormGroup;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the organisation ID.
     * @private
     */
    private readonly orgId: string;

    /**
     * Store the initial value of samlEnforced.
     * @private
     */
    private samlEnforcedInitValue: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private downloadService: DownloadService,
        private logService: LogService,
        private modalService: ModalService,
        private organisationApiService: OrganisationsApiService,
        private rbacService: RbacService,
        private userService: UserService
    ) {
        super();

        this.hasTier = this.rbacService.getPageRequiredRoute('/api/v1/organisations/:orgid/saml')?.tier;
        this.orgId = this.userService.getUser('organisationId').toString();
        this.setupFormGroup();
        this.setUpEnforceFormGroup();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Next step.
     */
    public next(): void {
        this.activeStep++;
    }

    /**
     * Back step.
     */
    public back(): void {
        this.activeStep--;
    }

    /**
     * Submit enforce.
     */
    public submitEnforce(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const formValues = this.enforceFormGroup.value;

            this.organisationApiService.update(this.orgId, formValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        // API is very quick, make it look like it is doing something!
                        const timerId = setTimeout((): void => {
                            this.samlEnforcedInitValue = formValues.samlEnforced;
                            this.lbHistoryUpdate(this.samlEnforcedInitValue);
                            this.setUpPageData();
                            observer.next();
                            observer.complete();
                            clearTimeout(timerId);
                        }, 1500);

                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'SamlComponent: submitEnforce',
                            'Error submitting data', error
                        );
                        observer.error(error);
                        observer.complete();
                    },
                });
        });
    }

    /**
     * Submit meta data.
     */
    public submit(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.organisationApiService.setUpSaml(this.orgId, this.formGroup.value)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.activeStep = 0;
                        this.resetForm();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'SamlComponent:submit',
                            'Unable to set up SAML.' + error
                        );
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Confirm remove adc.
     */
    public confirmDeleteSamlConfig(): void {
        const confirmationObject = {
            title: 'saml_remove_title',
            message: 'saml_remove_message',
            callBack: this.deleteSamlConfig.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * CTA to open intercom chat.
     */
    public getHelp(): void {
        const message: string = 'Hello! I\'m trying to setup SAML on the Portal. Can you provide assistance?';
        Intercom('showNewMessage', message);
    }

    /**
     * Download XML file.
     */
    public downloadXmlFile(): void {
        this.downloadService.generateDownload(this.getXmlFile(), 'idp-metadata.xml', { type: 'text/plain' });
    }

    /**
     * Set samlEnforced in lbHistory.
     * @param state
     * @private
     */
    private lbHistoryUpdate(state: boolean): void {
        const lbHistory: string = localStorage.getItem('lbHistory');
        const historyObject: any = JSON.parse(lbHistory);
        historyObject['samlEnforced'] = historyObject['samlEnforced'] ? !historyObject['samlEnforced'] : state;
        localStorage.setItem('lbHistory', JSON.stringify(historyObject));
    }

    /**
     * Remove SAML config
     * @private
     */
    private deleteSamlConfig(): void {
        forkJoin({
            updateOrg: this.organisationApiService.update(this.orgId, { samlEnforced: false }),
            deleteSaml: this.organisationApiService.deleteSamlConfig(this.orgId)
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.activeStep = 1;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SamlComponent:deleteSamlConfig',
                        'Unable to delete SAML config.' + error
                    );
                }
            });
    }

    /**
     * Get XML file.
     * @private
     */
    private getXmlFile(): Blob {
        /* eslint-disable */
        const xml = `
        <EntityDescriptor xmlns="urn:oasis:names:tc:SAML:2.0:metadata" xmlns:assertion="urn:oasis:names:tc:SAML:2.0:assertion" xmlns:ds="http://www.w3.org/2000/09/xmldsig#" entityID="${this.entityId}">

            <SPSSODescriptor AuthnRequestsSigned="false" WantAssertionsSigned="true" protocolSupportEnumeration="urn:oasis:names:tc:SAML:2.0:protocol">

                <NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress</NameIDFormat>

                <AssertionConsumerService index="0" Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST" Location="${this.ssoUrl}"/>

            </SPSSODescriptor>

        </EntityDescriptor>
        `;
        /* eslint-enable */

        return new Blob([xml], { type: 'text/plain' });
    }

    /**
     * Setup user form.
     * @private
     */
    private setupFormGroup(): void {
        this.formGroup = this.formBuilder.group({
            metaDataLink: [null, [Validators.required, urlValidation]]
        });
    }

    /**
     * Setup user form.
     * @private
     */
    private setUpEnforceFormGroup(): void {
        this.enforceFormGroup = this.formBuilder.group({
            samlEnforced: [null]
        });

        this.enforceFormGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    if (value.samlEnforced === this.samlEnforcedInitValue) {
                        this.enforceFormGroup.get('samlEnforced').markAsPristine();
                    }
                }
            });
    }

    /**
     * Reset form.
     * @private
     */
    private resetForm(): void {
        const metaDataLink: AbstractControl = this.formGroup.get('metaDataLink');
        metaDataLink.setValue(null);
        metaDataLink.markAsUntouched();
        metaDataLink.markAsPristine();
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.organisationApiService.get(this.orgId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (orgDetails: any): void => {
                    if (orgDetails?.ssoProvider === 'SAML') {
                        this.activeStep = 0;
                    }

                    if (orgDetails.samlEnforced !== null && orgDetails.samlEnforced !== undefined) {
                        this.samlEnforcedInitValue = orgDetails.samlEnforced;
                        this.enforceFormGroup.get('samlEnforced').setValue(orgDetails.samlEnforced);
                    }

                    this.isLoading = false;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SamlComponent:setUpPageData',
                        'Unable to get organisation SSO provider.' + error
                    );
                }
            });
    }

    /**
     * Register button permissions.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'saml-submit-config': [
                { path: '/api/v1/organisations/:orgid/saml', methods: ['POST'] }
            ],
            'saml-delete-config': [
                { path: '/api/v1/organisations/:orgid/saml', methods: ['DELETE'] }
            ]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
